import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { getStripe, getStripeEnvironment } from "@/lib/stripe";
import { supabase } from "@/integrations/supabase/client";

interface StripeEmbeddedCheckoutProps {
  priceId: string;
  customerEmail?: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  returnUrl: string;
  signup?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
  };
  /** Förskapad session — om satt görs inget extra anrop till backend. */
  clientSecret?: string;
}

/**
 * Skapar en checkout-session och returnerar clientSecret.
 * Kastar Error med backendens svenska felmeddelande vid t.ex. upptagen e-post.
 */
export async function createCheckoutClientSecret(
  body: Record<string, unknown>,
): Promise<string> {
  const { data, error } = await supabase.functions.invoke("create-checkout", {
    body: { ...body, environment: getStripeEnvironment() },
  });
  if (error || !data?.clientSecret) {
    let message = data?.error || "";
    if (!message && error && "context" in error) {
      try {
        const ctx = (error as any).context;
        if (ctx && typeof ctx.json === "function") {
          const parsed = await ctx.json();
          message = parsed?.error || "";
        }
      } catch {
        /* ignore */
      }
    }
    throw new Error(message || error?.message || "Kunde inte skapa betalningssession");
  }
  return data.clientSecret;
}

/**
 * Skapar en Stripe-hostad checkout-session (ui_mode: "hosted") och returnerar
 * URL:en som kunden ska omdirigeras till. Används i embed-läge så att
 * 3DS/BankID inte fastnar i iframe-i-iframe.
 */
export async function createHostedCheckoutUrl(
  body: Record<string, unknown>,
): Promise<string> {
  const { data, error } = await supabase.functions.invoke("create-checkout", {
    body: { ...body, hostedMode: true, environment: getStripeEnvironment() },
  });
  if (error || !data?.url) {
    let message = data?.error || "";
    if (!message && error && "context" in error) {
      try {
        const ctx = (error as any).context;
        if (ctx && typeof ctx.json === "function") {
          const parsed = await ctx.json();
          message = parsed?.error || "";
        }
      } catch {
        /* ignore */
      }
    }
    throw new Error(message || error?.message || "Kunde inte skapa betalningssession");
  }
  return data.url as string;
}

export function StripeEmbeddedCheckout(props: StripeEmbeddedCheckoutProps) {
  const { clientSecret, ...rest } = props;
  const options = clientSecret
    ? { clientSecret }
    : { fetchClientSecret: () => createCheckoutClientSecret(rest) };

  return (
    <div id="checkout">
      <EmbeddedCheckoutProvider stripe={getStripe()} options={options}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
}
